<script setup lang="ts">
interface Props {
  id?: string;
  value?: string;
  type?: string;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  autocomplete?: string;
  required?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  id: '',
  value: '',
  type: 'text',
  label: '',
  placeholder: '',
  error: '',
  disabled: false,
  autocomplete: 'off',
  required: false,
});

const emit = defineEmits<{
  input: [value: string];
}>();

function handleInput(event: Event) {
  const target = event.target as HTMLInputElement;
  emit('input', target.value);
}
</script>

<template>
  <div class="mb-2.5">
    <label v-if="label" :for="id || undefined" class="block text-white/80 text-sm font-medium mb-2">
      {{ label }}
      <span v-if="required" class="text-sw-error">*</span>
    </label>
    <input
      :id="id || undefined"
      :type="type"
      :value="value"
      :placeholder="placeholder"
      :disabled="disabled"
      :autocomplete="autocomplete"
      :required="required"
      :aria-describedby="error && id ? `${id}-error` : undefined"
      :aria-invalid="error ? 'true' : undefined"
      :class="[
        'w-full h-input px-[18px] rounded',
        'font-[SFUIText,system-ui,sans-serif] text-base font-normal',
        'text-white bg-input-bg',
        'outline-none transition-all duration-200',
        'placeholder:text-white/70',
        'hover:opacity-100',
        'focus:bg-input-bg-focus focus:opacity-100',
        error ? 'border border-sw-error' : 'border border-transparent',
        disabled ? 'opacity-50 cursor-not-allowed' : 'opacity-70 cursor-text',
      ]"
      @input="handleInput"
    />
    <p
      v-if="error"
      :id="id ? `${id}-error` : undefined"
      class="text-sw-error text-[0.9rem] leading-[1.82] m-0 mt-1"
      role="alert"
    >
      {{ error }}
    </p>
  </div>
</template>
